import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
  ViewContainerRef,
  WritableSignal,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { I18nState } from '@app/core/states/i18n.state';
import { RadioState } from '@app/core/states/radio.state';
import { aLogEvent, AMPLITUDE_EVENT } from '@app/shared/amplitude-events';
import { ColorUtils } from '@app/shared/utils';
import { FullCalendarComponent } from '@fullcalendar/angular';
import {
  Calendar,
  CalendarOptions,
  ConstraintInput,
  DateSelectArg,
  DateSpanApi,
  DayHeaderContentArg,
  DurationInput,
  EventApi,
  EventClickArg,
  EventContentArg,
  EventDropArg,
  EventMountArg,
  EventSourceFunc,
  SlotLaneContentArg,
} from '@fullcalendar/core';
import { VerboseFormattingArg } from '@fullcalendar/core/internal';
import frLocale from '@fullcalendar/core/locales/fr';
import interactionPlugin, { EventResizeDoneArg } from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import timeGridPlugin from '@fullcalendar/timegrid';
import { ResizeObserver } from '@juggle/resize-observer';
import { TranslateService } from '@ngx-translate/core';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { ToasterService } from '@radioking/shared/common-services';
import {
  PanelContainerInput,
  PanelRegistryService,
  PANELS_KEYS,
} from '@radioking/shared/pannel-manager';
import { SugestionInput } from '@radioking/ui/input';
import { omit, throttle } from 'lodash';
import moment from 'moment-timezone';
import { combineLatest, interval, Observable, Subscription } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  startWith,
  take,
  takeUntil,
  throttleTime,
} from 'rxjs/operators';

import { AppState } from '@app/core/states/app.state';
import { MOBILE_DIALOG_CONFIG } from '@app/shared/constants';
import { ProgramsRequest } from '../../../../../programs/src/lib/states/programs.actions';
import { ProgramsState } from '../../../../../programs/src/lib/states/programs.state';
import {
  SchedulerAllPlaylistsRequest,
  SchedulerAllTracksRequest,
} from '../../../../../scheduler-core/src/lib/states/scheduler-core.actions';
import { SchedulerCoreState } from '../../../../../scheduler-core/src/lib/states/scheduler-core.state';
import { DateSelectDatesChanged } from '../../models/date-select.model';
import {
  CalendarDatesChanged,
  DatesRange,
  Slot,
  SlotKind,
} from '../../models/planning.model';
import { planningToasts } from '../../planning.toasts';
import {
  AddSlotFromSidenav,
  GetPlanningRequest,
  GetPlanningSuccess,
  PlanningCreateSlotSuccess,
  PlanningDeleteSlotSuccess,
  PlanningDropSlotRequest,
  PlanningEditSlotFailure,
  PlanningEditSlotSuccess,
  PlanningMoveSlotRequest,
  SetCurrentDay,
  SlotDropSameDayOnly,
  TooltipNoContent,
} from '../../states/planning.actions';
import { PlanningState } from '../../states/planning.state';
import { PlanningDayHeaderComponent } from '../planning-day-header/planning-day-header.component';
import { PlanningSlotAddComponent } from '../planning-slot-add/planning-slot-add.component';
import { PlanningSlotDeletionComponent } from '../planning-slot-deletion/planning-slot-deletion.component';
import { PlanningSlotEditionComponent } from '../planning-slot-edition/planning-slot-edition.component';
import { PlanningSlotMoveComponent } from '../planning-slot-move/planning-slot-move.component';
import { PlanningSlotPopoverComponent } from '../planning-slot-popover/planning-slot-popover.component';

/**
 * Duration of an slot in minutes, by "tabview"
 */
const SLOT_DURATION_IN_MINUTES = 15;

/**
 * Restrict slots to one day
 */
const SLOT_RESTRICTED_TO_ONE_DAY: Readonly<ConstraintInput> = {
  startTime: '00:00',
  endTime: '24:00',
};

/**
 * Number of hours negative offset to now, when scrolling to now
 *
 * Based on {@link SLOT_DURATION_IN_MINUTES}
 */
const SCROLL_TO_NOW_HOURS_BEFORE_OFFSET = 2;

@Component({
  selector: 'rk-planning-shell',
  templateUrl: './planning-shell.component.html',
  styleUrls: ['./planning-shell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanningShellComponent implements AfterViewInit, OnInit, OnDestroy {
  sides: PanelContainerInput[] = [
    {
      key: PANELS_KEYS.PLANNING_SLOT_ADD,
      val: PlanningSlotAddComponent,
    },
  ];

  @Select(RadioState.currentRadioId)
  radioId$: Observable<number>;

  @Select(ProgramsState.allProgramsForDropdown)
  allPrograms$: Observable<SugestionInput[]>;

  @Select(SchedulerCoreState.allPlaylistsForDropdown)
  allPlaylists$: Observable<SugestionInput[]>;

  @Select(PlanningState.slots)
  slots$: Observable<Slot[]>;

  @Select(PlanningState.currentDay)
  currentDay$: Observable<moment.Moment>;

  @Select(PlanningState.datesRange)
  datesRange$: Observable<DatesRange>;

  @Select(SchedulerCoreState.hasNotEmptyPlaylists)
  hasNotEmptyPlaylists$: Observable<boolean>;

  @Select(ProgramsState.hasNotEmptyPrograms)
  hasNotEmptyPrograms$: Observable<boolean>;

  @Select(RadioState.currentRadioTimezone)
  radioTz$: Observable<string>;

  @Select(AppState.onMobile)
  onMobile$: Observable<boolean>;

  @Select(I18nState.currentLang)
  currentLang$: Observable<string>;

  @Output()
  readonly calendarDatesChanged = new EventEmitter<CalendarDatesChanged>(true);

  $tinyHeader: WritableSignal<boolean> = signal(false);
  $scrolledToNow: WritableSignal<boolean> = signal(false);
  // Used to detect scrolling direction
  scrollTop = 0;
  // Used to determine max scroll down level, to put back big header
  scrollDownLevel = 0;

  /**
   * Fullcalendar options
   */
  readonly calendarOptions: CalendarOptions = {
    plugins: [interactionPlugin, timeGridPlugin, momentTimezonePlugin],
    headerToolbar: false,
    initialView: 'timeGridWeek',
    navLinks: false, // Could enhance UX when set to true (development required to sync view selector)
    allDaySlot: false,
    locales: [frLocale],
    slotDuration: {
      minutes: SLOT_DURATION_IN_MINUTES,
    },
    slotLabelInterval: {
      minutes: SLOT_DURATION_IN_MINUTES,
    },
    slotLabelFormat: this.formatSlotLabel.bind(this),
    dayHeaderContent: this.formatDayHeader.bind(this),
    dayHeaderFormat: {
      weekday: 'short',
      month: 'numeric',
      day: 'numeric',
      omitCommas: true,
    },
    eventTimeFormat: this.formatEventTime.bind(this),
    eventClassNames: this.handleEventClassNames.bind(this),
    eventContent: this.handleEventContent.bind(this),
    eventDidMount: this.handleEventDidMount.bind(this),
    scrollTime: null, // Always preserve current scroll
    nowIndicator: true,
    businessHours: false,
    // initialEvents, // TODO Alternatively, use the `events` setting to fetch from a feed
    firstDay: 1, // First day of week = monday
    weekends: true,
    editable: true,
    selectable: true,
    selectMirror: true,
    droppable: true,
    eventsSet: this.handleEvents.bind(this),
    selectConstraint: SLOT_RESTRICTED_TO_ONE_DAY,
    eventConstraint: SLOT_RESTRICTED_TO_ONE_DAY,
    selectOverlap: this.handleEventOverlap.bind(this),
    eventOverlap: this.handleEventOverlap.bind(this),
    selectAllow: this.handleSelectAllow.bind(this),
    eventAllow: this.handleEventAllow.bind(this),
    datesSet: this.emitDatesChanged.bind(this),
    // select: this.handleDateSelect.bind(this),
    eventClick: this.handleEventClick.bind(this),
    eventDrop: this.handleEventDrop.bind(this),
    eventResize: this.handleEventResize.bind(this),
    eventDragStart: this.eventDragStart.bind(this),
    eventDragStop: this.eventDragStop.bind(this),
    eventReceive: this.eventReceive.bind(this),
    eventSources: [
      {
        id: 'slots',
        events: this.fetchSlots.bind(this),
      },
    ],
    eventMinHeight: 27,
    eventColor: '#ff7f50',
    longPressDelay: 200, // long press to trigger DnD
  };

  currentEvents: EventApi[] = [];

  @ViewChild('calendar') calendarComponent: FullCalendarComponent;

  /**
   * Update size of calendar (rendering) when resized
   */
  calendarResizeObserver = new ResizeObserver(
    throttle(() => this.calendarUpdateSize(), 250),
  );

  /**
   * Enable smooth scrolling of calendar
   *
   * UX: Should be activated AFTER calendar's initial scrolling
   */
  smoothScrolling = false;

  /**
   * Whether scrolling of calendar should be prevented or not
   *
   * E.g.: When a dialog is opened, scrolling should be prevented
   */
  shouldPreventScrolling = false;

  /**
   * Popover's dialog reference when opened
   */
  popoverDialogRef: MatDialogRef<PlanningSlotPopoverComponent>;

  subscription = new Subscription();

  @ViewChild('dayHeaderContentContainer', { read: ViewContainerRef })
  dayHeaderContentContainer: ViewContainerRef;

  dayHeaderComponents: ComponentRef<PlanningDayHeaderComponent>[] = [];

  eventsArray: HTMLElement[] = [];
  scroller: Element;

  constructor(
    private readonly dialog: MatDialog,
    private readonly toaster: ToasterService,
    private readonly panelRegistry: PanelRegistryService,
    private readonly componentFactory: ComponentFactoryResolver,
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly translate: TranslateService,
  ) {
    this.panelRegistry.registerMultiplePanels(this.sides, this.componentFactory);
  }

  /**
   * Get calendar plugin's api (fullcalendar)
   */
  get calendarApi(): Calendar {
    return this.calendarComponent?.getApi();
  }

  ngOnInit(): void {
    this.toaster.registerToasts(planningToasts);
    this.listenToEventsRefresh();
    this.listenToRadioSwitch();
  }

  ngAfterViewInit(): void {
    this.subscription.add(this.currentLang$.subscribe(locale => this.setLocale(locale)));
    this.calendarScrollToNow();
    this.subscription.add(
      this.onMobile$.subscribe(m => {
        this.calendarApi.changeView(m ? 'timeGridDay' : 'timeGridWeek');
      }),
    );
    this.smoothScrolling = true;
    this.calendarResizeObserver.observe(this.calendarComponent.getApi().el);
    this.setTimezone();
    this.setNotEmpty();

    this.actions$.pipe(ofActionDispatched(AddSlotFromSidenav)).subscribe(({ slot }) => {
      const partialEvent = this.defaultCreationEvent();
      partialEvent.extendedProps = slot.extendedProps;
      this.slotCreation(partialEvent);
    });

    interval(30000).subscribe(() => {
      // this.setNowIndicator();
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.calendarResizeObserver.disconnect();
    this.panelRegistry.unregisterMultiplePanels(this.sides);
  }

  /**
   * Fetch events/slots from backend
   */
  fetchSlots(
    fetchInfo: Parameters<EventSourceFunc>[0],
    successCallback: Parameters<EventSourceFunc>[1],
    failureCallback: Parameters<EventSourceFunc>[2],
  ): void {
    this.subscription.add(
      combineLatest([this.allPrograms$, this.allPlaylists$, this.slots$])
        .pipe(
          filter(([programs, playlists, slots]) => !!programs && !!playlists && !!slots),
          take(1),
        )
        .subscribe(([programs, playlists, slots]) => {
          successCallback(this.addSlotsToPlanning(slots, programs, playlists));
        }),
    );
  }

  /**
   * Convert all slots/occurrences to individual planning slots
   */
  addSlotsToPlanning(
    slots: Slot[],
    programs: SugestionInput[],
    playlists: SugestionInput[],
  ): object[] {
    this.calendarApi?.removeAllEvents();
    this.eventsArray = [];
    const finalSlots: any[] = [];
    if (slots) {
      slots.map(slot => {
        const title = slot.program
          ? programs.find(v => v.id === slot.program).name
          : playlists.find(v => v.id === slot.playlist).name;
        const color = slot.program
          ? programs.find(v => v.id === slot.program).color
          : playlists.find(v => v.id === slot.playlist).color;

        if (slot.occurrences) {
          const occurs = slot.occurrences.map(occ => {
            return {
              id: String(occ.id),
              title,
              start: occ.startDate.toDate(),
              end: occ.endDate.toDate(),
              color,
              textColor: ColorUtils.bestContrastTextColor(color),
              display: 'auto',
              extendedProps: { ...slot, type: 'slot' },
            };
          });
          finalSlots.push(...occurs);
        }
      });
      this.scrollSticky();
    }

    return finalSlots;
  }

  scrollSticky() {
    this.scroller = document.querySelector(
      'full-calendar > div > div > table > tbody > tr > td div.fc-scroller',
    );
    if (this.scroller) {
      this.scroller.addEventListener('scroll', (e: Event) => {
        this.stickyEventName(this.eventsArray);

        // Update tiny header, after first scroll to current time
        if (this.$scrolledToNow()) {
          const eventTarget = event.target as HTMLElement;

          if (eventTarget.scrollTop > this.scrollTop && eventTarget.scrollTop > 90) {
            // scrolling down
            this.scrollDownLevel = eventTarget.scrollTop;
            this.$tinyHeader.set(true);
          } else if (
            eventTarget.scrollTop < this.scrollTop &&
            this.scrollDownLevel - eventTarget.scrollTop > 40
          ) {
            // scrolling up
            this.$tinyHeader.set(false);
          }
          this.scrollTop = eventTarget.scrollTop;
        }
      });
    }
  }

  stickyEventName(events: HTMLElement[]) {
    const topValue = this.store.selectSnapshot(AppState.onMobile)
      ? this.$tinyHeader()
        ? '182px'
        : '222px'
      : '254px';
    events.forEach(event => {
      const eventContent = event?.querySelector('.fc-event-main-frame') as HTMLElement;
      if (eventContent) {
        if (
          event.offsetTop < this.scroller.scrollTop - 6 &&
          event.offsetHeight > 50 &&
          event.offsetTop + event.offsetHeight - eventContent.offsetHeight >
            this.scroller.scrollTop
        ) {
          eventContent.style.position = 'fixed';
          eventContent.style.top = topValue;
        } else {
          eventContent.style.position = 'initial';
        }
      }
    });
  }

  /**
   * Listen to events refresh, which should be called in some situations,
   * in order to sync with backend
   *
   * Fetch up-to-date events from backend
   */
  listenToEventsRefresh(): void {
    this.subscription.add(
      this.actions$
        .pipe(
          ofActionDispatched(
            PlanningCreateSlotSuccess,
            PlanningEditSlotSuccess,
            PlanningEditSlotFailure, // Mostly for reverting event d&d and resizing failure
            PlanningDeleteSlotSuccess,
            GetPlanningSuccess,
          ),
        )
        .subscribe(() => this.calendarApi.refetchEvents()),
    );
    this.subscription.add(
      this.currentDay$.subscribe(currentDay => {
        if (currentDay) {
          this.calendarApi?.gotoDate(currentDay.toDate());
        }
      }),
    );
  }

  /**
   * Listen to radio switch
   */
  listenToRadioSwitch(): void {
    this.subscription.add(
      this.radioId$
        .pipe(
          filter(id => !!id),
          distinctUntilChanged(),
        )
        .subscribe(id => {
          this.getPrograms();
          this.getPlaylists(id);
          this.getTracks(id);
        }),
    );

    this.subscription.add(
      combineLatest([
        this.radioId$.pipe(
          filter(id => !!id),
          distinctUntilChanged(),
        ),
        this.calendarDatesChanged.pipe(
          startWith(null),
          pairwise(),
          filter(
            ([prev, curr]) =>
              prev === null || prev.formattedRange !== curr.formattedRange,
          ),
          map(([prev, curr]) => curr),
        ),
        this.datesRange$,
        this.radioTz$.pipe(filter(tz => !!tz)),
      ])
        .pipe(throttleTime(200))
        .subscribe(([id, dates, range, tz]) => {
          // Planning is fetched from the setCurrentDay action on mobile
          if (!range || !this.store.selectSnapshot(AppState.onMobile)) {
            const adjustedStartDate = moment(dates.start).add(1, 'day').tz(tz); // We add 1 day to prevent wrong week for negative tz offsets
            this.getPlanning(
              adjustedStartDate.clone().startOf('week'),
              adjustedStartDate.clone().endOf('week').add(1, 'day'),
            );
          }
        }),
    );
  }

  /**
   * Set calendar's locale
   */
  setLocale(locale: string): void {
    this.calendarApi.setOption('locale', locale);
  }

  setTimezone(): void {
    this.subscription.add(
      combineLatest([this.radioTz$.pipe(filter(tz => !!tz)), this.datesRange$]).subscribe(
        ([tz, r]) => {
          this.calendarApi.setOption('timeZone', tz);
          this.setNowIndicator();
        },
      ),
    );
  }

  setNowIndicator() {
    const tz = this.calendarApi.getOption('timeZone');
    const fcNowIndicator = document.querySelector(
      '.fc-timegrid-now-indicator-line',
    ) as HTMLElement;

    if (fcNowIndicator && tz) {
      const nowTZ = moment().tz(tz);
      // Compute the offset in hours between local time and the calendar's timezone
      const tzOffset = (nowTZ.utcOffset() - moment().utcOffset()) / 60;
      fcNowIndicator.style.marginTop = `${tzOffset * 120}px`; // 120 because each hour is 4 cells of 30px
      fcNowIndicator.style.display = 'block';
    }
  }

  setNotEmpty(): void {
    this.subscription.add(
      combineLatest([
        this.hasNotEmptyPlaylists$,
        this.hasNotEmptyPrograms$,
        this.onMobile$,
      ]).subscribe(([pl, pr, m]) => {
        if (pr || pl) {
          if (!m) {
            this.calendarApi.setOption('slotLaneContent', this.formatSlotLane.bind(this));
          }
          this.calendarApi.setOption('select', this.handleDateSelect.bind(this));
        }
      }),
    );
  }

  /**
   * Selected date changed
   */
  dateSelectDatesChanged(event: DateSelectDatesChanged): void {
    this.calendarApi.gotoDate(event.start);
  }

  /**
   * Go to calendar's previous period
   */
  calendarPrevious(): void {
    this.calendarApi.prev();
  }

  /**
   * Go to calendar's next period
   */
  calendarNext(): void {
    this.calendarApi.next();
  }

  /**
   * Go to calendar's today period
   */
  calendarToday(): void {
    const now = moment();
    const calendarDate = this.calendarApi.getDate();

    // Scroll to now when today is already being displayed
    const shouldScrollToNow = now.isSame(calendarDate, 'week');

    // When going to today in week view, already being on today's week,
    // but calendar's date is not today: fullcalendar will not trigger "datesSet" event
    const shouldEmitDatesChanged =
      now.isSame(calendarDate, 'week') && !now.isSame(calendarDate, 'day');

    this.calendarApi.today();

    if (shouldScrollToNow) {
      this.calendarScrollToNow();
    }

    if (shouldEmitDatesChanged) {
      this.emitDatesChanged();
    }
  }

  /**
   * Scroll to "now" in calendar
   */
  calendarScrollToNow(): void {
    this.calendarApi.scrollToTime(this.nowScrollTime());
    setTimeout(() => {
      if (this.scroller) {
        // Trigger scroll event to update tiny header
        // Note: this is a workaround to trigger scroll event
        const scrollEvent = new Event('scroll', { bubbles: true });
        this.scroller.dispatchEvent(scrollEvent);
        this.$scrolledToNow.set(true);
      }
    }, 1000);
  }

  /**
   * Compute and return position of "now" when scrolling to "now"
   *
   * This should return a position slightly before "now" for a better UX,
   * ideally the "now indicator" should be centered vertically in calendar
   *
   * See {@link SCROLL_TO_NOW_HOURS_BEFORE_OFFSET}
   * See {@link SLOT_DURATION_IN_MINUTES}
   */
  nowScrollTime(): DurationInput {
    return { hours: new Date().getHours() - SCROLL_TO_NOW_HOURS_BEFORE_OFFSET };
  }

  /**
   * Update size of calendar (rendering) automatically
   */
  calendarUpdateSize(): void {
    this.calendarApi.updateSize();
    if (this.store.selectSnapshot(RadioState.currentRadioTimezone)) {
      this.setNowIndicator();
    }
  }

  /**
   * Format a slot lane of calendar (one row per slot interval)
   */
  formatSlotLane(arg: SlotLaneContentArg): { html: string } {
    // Note: this is creating a lot of DOM nodes, which is decreasing performanmances
    // See https://github.com/fullcalendar/fullcalendar/issues/4816 to know why it was not originally implemented
    const count = arg.view.type === 'timeGridWeek' ? 7 : 1;
    const buttons = '<button class="fc-timegrid-slot-lane-plus-button">+</button>'.repeat(
      count,
    );

    return {
      html: `<div class="fc-timegrid-slot-lane-plus-button-wrapper">${buttons}</div>`,
    };
  }

  /**
   * Format a slot label of calendar (slot interval's time)
   */
  formatSlotLabel(arg: VerboseFormattingArg): string {
    return moment(arg.date).format('LT');
  }

  /**
   * Format a day header of calendar (one cell per day)
   */
  formatDayHeader(arg: DayHeaderContentArg): { domNodes: HTMLElement[] } {
    // Initialize day header components
    if (!this.dayHeaderComponents.length) {
      const componentFactory = this.componentFactory.resolveComponentFactory(
        PlanningDayHeaderComponent,
      );

      for (let i = 1; i <= 7; i++) {
        this.dayHeaderComponents.push(
          this.dayHeaderContentContainer.createComponent(componentFactory),
        );
      }
    }

    const component = this.dayHeaderComponents[arg.dow].instance;
    component.dayHeaderContentArg$.next(arg);

    return {
      domNodes: [component.getElement()],
    };
  }

  /**
   * Format an event/slot time
   */
  formatEventTime(arg: VerboseFormattingArg): string {
    return (
      moment(arg.start).format('LT') +
      this.translate.instant('SCHEDULE.PLANNING.TIME_SEPARATOR') +
      moment(arg.end).format('LT')
    );
  }

  /**
   * Custom classes for an event/slot DOM node
   */
  handleEventClassNames(arg: EventContentArg): string[] {
    const classes = [];

    // Event type
    classes.push(`fc-event-extended-type-${arg.event.extendedProps.type ?? 'none'}`);

    // Slot kind
    classes.push(`fc-event-extended-kind-${arg.event.extendedProps.kind ?? 'none'}`);

    // Event repeat
    if (
      arg.event.extendedProps?.occurrences &&
      arg.event.extendedProps.occurrences.length > 1
    ) {
      classes.push('fc-event-extended-repeat');
    }

    // Contrast
    classes.push(
      `fc-event-extended-contrast-${arg.textColor === '#fff' ? 'light' : 'dark'}`,
    );

    return classes;
  }

  /**
   * DOM content for an slot
   *
   * Reuse fullcalendar original content and enhance with custom content
   */
  handleEventContent(arg: EventContentArg): { html: string } {
    const mStart = moment(arg.event.start);
    const mEnd = moment(arg.event.end);

    return {
      html: `
<div class="fc-event-main-frame ${
        mEnd.diff(mStart, 'minutes') < 19 ? 'fc-event-short' : ''
      }">
  <div class="fc-event-title-container min-w-0">
    <div class="fc-event-title fc-sticky">${arg.event.title}</div>
  </div>
  <div class="fc-event-time">${arg.timeText}</div>
</div>`,
    };
  }

  /**
   * Called when slot DOM node is added to DOM tree
   *
   * Note: this will not be called when event is re-rendered, which is not a problem at the moment
   */
  handleEventDidMount(arg: EventMountArg): void {
    // Event type for event's harness DOM node (because css :has() is not supported yet)
    arg.el
      .closest('.fc-timegrid-event-harness')
      // Use an attribute instead of a class because class will be overwritten by fullcalendar in some situations like event re-rendering
      .setAttribute(
        `data-has-fc-event-extended-type-${arg.event.extendedProps.type ?? 'none'}`,
        '',
      );
    arg.el.oncontextmenu = e => {
      // Prevent default context menu on long press, needed for DnD
      if (this.store.selectSnapshot(AppState.onMobile)) {
        return false;
      }
      const clickInfo: EventClickArg = {
        el: arg.el,
        view: arg.view,
        event: arg.event,
        jsEvent: e,
      };
      this.handleEventClick(clickInfo);
      return false;
    };
    this.eventsArray.push(arg.el.parentElement);
    if (this.scroller) {
      this.stickyEventName([arg.el.parentElement]);
    }
  }

  /**
   * Called when slots are initialized/modified
   */
  handleEvents(events: EventApi[]): void {
    this.currentEvents = events;
  }

  /**
   * Whether an slot overlapping is allowed or not
   * Called when creating/moving/resizing an event
   *
   */
  handleEventOverlap(stillEvent: EventApi, movingEvent: EventApi | null): boolean {
    return !movingEvent;
  }

  handleSelectAllow(): boolean {
    if (
      this.store.selectSnapshot(ProgramsState.hasNotEmptyPrograms) ||
      this.store.selectSnapshot(SchedulerCoreState.hasNotEmptyPlaylists)
    ) {
      return true;
    }
    return false;
  }

  /**
   * Constraints when creating/moving/resizing an slot
   */
  handleEventAllow(dropInfo: DateSpanApi, draggedEvent: EventApi | null): boolean {
    return true;
  }

  /**
   * Restrict Drag to same day
   */
  eventDragStart(info: any) {
    const currentCol = info.el.closest('td');
    const allCols = info.el.closest('tr').querySelectorAll('td[role="gridcell"]');
    Array.from(allCols).forEach(col => {
      if (col !== currentCol) {
        col['style']['backgroundColor'] = '#f8f9f9';
      }
    });
    const radioTz = this.store.selectSnapshot(RadioState.currentRadioTimezone);
    const evStart = moment((info.event as EventApi).start).tz(radioTz);
    this.calendarApi.setOption('eventConstraint', {
      startTime: '00:00',
      endTime: '24:00',
      daysOfWeek: [(evStart.get('weekday') + evStart.localeData().firstDayOfWeek()) % 7],
    });
  }

  eventDragStop(info: any) {
    const allCols = info.el.closest('tr').querySelectorAll('td[role="gridcell"]');
    Array.from(allCols).forEach(col => {
      col['style']['backgroundColor'] = '';
    });
    this.calendarApi.setOption('eventConstraint', {});
  }

  /**
   * Used for DnD from sidenav
   * @param info
   */
  eventReceive(info: any) {
    const itemId = Number.parseInt(
      (info.draggedEl as HTMLElement).getAttribute('data-id'),
      10,
    );
    const el = info.draggedEl;
    const isPlaylist = el.classList.contains('fc-playlist');
    const slot: Partial<Slot> = {
      kind: isPlaylist ? SlotKind.playlist : SlotKind.program,
      start: moment(info.event.start),
      end: moment(info.event.end),
    };
    if (isPlaylist) {
      slot.playlist = itemId;
    } else {
      slot.program = itemId;
    }
    this.dropSlot(slot);
  }
  /**
   * Dates of calendar changed (changing view, going to previous/next period, etc)
   */
  emitDatesChanged(): void {
    const current = this.calendarApi.getDate();
    const start = this.calendarApi.view.activeStart;
    const end = this.calendarApi.view.activeEnd;
    const formatOptions = {
      isEndExclusive: true,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    };
    const formattedRange = this.calendarApi.formatRange(start, end, formatOptions);
    this.calendarDatesChanged.emit({ current, start, end, formattedRange });
  }

  /**
   * Selection of a date range in calendar, to create a new slot
   */
  handleDateSelect(selectInfo: DateSelectArg): void {
    const eventInRange = this.calendarApi
      .getEvents()
      .find(ev => ev.start <= selectInfo.start && ev.end > selectInfo.start);
    const start = eventInRange?.end ?? selectInfo.start;
    const end = new Date();
    end.setTime(start.getTime() + 2 * 60 * 60 * 1000);
    // Default to 2 hours after start date, will be overwritten with first playlist duration
    this.calendarApi.unselect();
    aLogEvent(AMPLITUDE_EVENT.PLANNING.CTA_ADD_INSIDE);
    this.slotCreation({ start, end });
  }

  /**
   * Slot clicked: show a popover with details
   */
  handleEventClick(clickInfo: EventClickArg): void {
    this.handleSlotPopover(clickInfo);
  }

  /**
   * Show a popover with slot details
   */
  handleSlotPopover(clickInfo: EventClickArg): void {
    const mobile = this.store.selectSnapshot(AppState.onMobile);
    const dialogConfig = mobile
      ? {
          data: { clickInfo, mobile: true },
          closeOnNavigation: true,
          ...MOBILE_DIALOG_CONFIG,
        }
      : {
          data: { clickInfo },
          closeOnNavigation: true,
          hasBackdrop: false, // See closeOpenedDialogsOnOutsideClick()
          panelClass: 'cdk-overlay-pane-transparent-dialog',
        };
    const dialogRef = this.dialog.open(PlanningSlotPopoverComponent, dialogConfig);

    this.subscription.add(
      dialogRef.componentInstance.slotDeletion
        .pipe(takeUntil(dialogRef.afterClosed()))
        .subscribe(event => this.slotDeletion(event, mobile)),
    );

    this.subscription.add(
      dialogRef.componentInstance.slotEdition
        .pipe(takeUntil(dialogRef.afterClosed()))
        .subscribe(event => this.slotEdition(event, mobile)),
    );

    this.subscription.add(
      dialogRef.afterOpened().subscribe(() => {
        this.toggleEventActiveClass(clickInfo.el, true);
        this.shouldPreventScrolling = true;
        this.popoverDialogRef = dialogRef;
      }),
    );

    this.subscription.add(
      dialogRef.afterClosed().subscribe(() => {
        this.toggleEventActiveClass(clickInfo.el, false);
        this.shouldPreventScrolling = false;
        this.popoverDialogRef = null;
      }),
    );
  }

  /**
   * Mark an slot as active or not
   *
   * Being active, an slot should be highlighted
   */
  toggleEventActiveClass(eventElement: Element, force?: boolean): void {
    eventElement.classList.toggle('fc-event-extended-active', force);
  }

  /**
   * Called when slots are moved
   */
  handleEventDrop(arg: EventDropArg): void {
    const radioTz = this.store.selectSnapshot(RadioState.currentRadioTimezone);

    // Limit drop to same day
    if (
      !moment(arg.oldEvent.start)
        .tz(radioTz)
        .isSame(moment(arg.event.start).tz(radioTz), 'day')
    ) {
      this.calendarApi.refetchEvents();
      this.store.dispatch(new SlotDropSameDayOnly());
    } else {
      this.updateEventDatesAfterMovingOrResizing(arg.event);
    }
  }

  /**
   * Called when slots are resized
   */
  handleEventResize(arg: EventResizeDoneArg): void {
    this.updateEventDatesAfterMovingOrResizing(arg.event);
  }

  /**
   * Update slot dates in extended props when it's moved or resized
   */
  updateEventDatesAfterMovingOrResizing(event: EventApi): void {
    const start = moment(event.start);
    const end = moment(event.end);
    start.set({
      date: event.extendedProps.start.date(),
      month: event.extendedProps.start.month(),
    });
    end.set({
      date: event.extendedProps.end.date(),
      month: event.extendedProps.end.month(),
    });
    // Offset is used to set dest time without day manipulation
    event.setExtendedProp(
      'startOffset',
      start.diff(event.extendedProps.start, 'minutes'),
    );
    event.setExtendedProp('endOffset', end.diff(event.extendedProps.end, 'minutes'));

    const slot = omit(event.extendedProps, ['type']) as Slot;
    if (slot.occurrences.length < 2) {
      slot.start = start;
      slot.end = end;
      this.store.dispatch(new PlanningMoveSlotRequest(true, slot, null));
    } else {
      const mobile = this.store.selectSnapshot(AppState.onMobile);
      const dialogConfig = mobile
        ? {
            data: event,
            closeOnNavigation: true,
            ...MOBILE_DIALOG_CONFIG,
          }
        : {
            data: event,
            closeOnNavigation: true,
          };
      this.dialog
        .open(PlanningSlotMoveComponent, dialogConfig)
        .afterClosed()
        .subscribe(cancelled => {
          if (cancelled || cancelled === undefined) {
            this.calendarApi.refetchEvents();
          }
        });
    }
  }

  /**
   * Handle closing of opened popovers without a backdrop for UX reasons
   *
   * - Having a visible backdrop would imply more clicks for users (to close the backdrop in the first place)
   * - Having a transparent backdrop would be even worse (more clicks, plus users cannot expect this behavior,
   * because the backdrop is invisible)
   */
  @HostListener('document:mouseup', ['$event'])
  closeOpenedPopoversOnOutsideClick(event: MouseEvent): void {
    if (
      this.popoverDialogRef &&
      !(event.target as HTMLElement).closest('.mat-dialog-container')
    ) {
      this.closeAllDialogs();
    }
  }

  /**
   * Prevent scrolling of calendar
   */
  @HostListener('wheel', ['$event'])
  @HostListener('touchmove', ['$event'])
  preventScrolling(event: WheelEvent): void {
    if (this.shouldPreventScrolling) {
      event.preventDefault();
    }
  }

  /**
   * Delete an event/slot
   */
  slotDeletion(data: EventApi, mobile = false): void {
    this.closeAllDialogs();

    this.dialog.open(
      PlanningSlotDeletionComponent,
      mobile
        ? {
            data,
            closeOnNavigation: true,
            ...MOBILE_DIALOG_CONFIG,
          }
        : {
            data,
            closeOnNavigation: true,
          },
    );
  }

  /**
   * Create an event/slot
   */
  slotCreation(tmpEvent: Partial<EventApi>): void {
    // If no programs or playlists, show a tooltip
    if (
      !(
        this.store.selectSnapshot(SchedulerCoreState.hasNotEmptyPlaylists) ||
        this.store.selectSnapshot(ProgramsState.hasNotEmptyPrograms)
      )
    ) {
      this.tooltipNoContent();
      return;
    }

    this.closeAllDialogs();
    const event = tmpEvent ? tmpEvent : this.defaultCreationEvent();

    const mobile = this.store.selectSnapshot(AppState.onMobile);

    aLogEvent(AMPLITUDE_EVENT.PLANNING.NEW);
    const creationDialog = this.dialog.open(
      PlanningSlotEditionComponent,
      mobile
        ? {
            data: { event, mobile: true },
            closeOnNavigation: true,
            ...MOBILE_DIALOG_CONFIG,
            panelClass: 'full-mobile-dialog',
            disableClose: true, // Prevent closing on outside click while opening, overlap with fullcalendar select event
          }
        : {
            data: { event },
            closeOnNavigation: true,
          },
    );
    creationDialog.afterOpened().subscribe(() => {
      creationDialog.disableClose = false;
    });
  }

  /**
   * Default event/slot for creation
   *
   * Starts on next hour when today, on midnight otherwise
   * Lasts 1 hour
   */
  defaultCreationEvent(): Partial<EventApi> {
    const calendarDate = moment(this.calendarApi.getDate()).tz(
      this.store.selectSnapshot(RadioState.currentRadioTimezone),
    );
    const isToday = moment().isSame(calendarDate, 'day');
    const start = (
      isToday
        ? calendarDate.clone().add(1, 'hour').startOf('hour')
        : calendarDate
            .clone()
            .startOf('day')
            .add(Math.round(this.scroller.scrollTop / 30) * 15, 'minute')
    ).toDate();
    const end = moment(start).add(1, 'hour').toDate();

    return {
      start,
      end,
    };
  }

  /**
   * Edit an event/slot
   */
  slotEdition(event: EventApi, mobile = false): void {
    this.closeAllDialogs();

    this.dialog.open(
      PlanningSlotEditionComponent,
      mobile
        ? {
            data: { event, mobile: true },
            closeOnNavigation: true,
            ...MOBILE_DIALOG_CONFIG,
            panelClass: 'full-mobile-dialog',
          }
        : {
            data: { event },
            closeOnNavigation: true,
          },
    );
  }

  onUnTinyHeader(): void {
    // Scroll to up to 50px to prevent triggering other scroll when on bottom of calendar
    if (
      this.scroller.clientHeight + this.scroller.scrollTop >=
      this.scroller.scrollHeight - 50
    ) {
      this.scroller.scrollTop = this.scroller.scrollTop - 50;
    }
    this.$tinyHeader.set(false);
    this.scroller.scrollTop = this.scroller.scrollTop - 1; // Force trigger scroll event to update sticky event names
  }

  /**
   * Close all dialogs
   */
  closeAllDialogs(): void {
    this.dialog.closeAll();
  }

  /**
   * Retrieve all programs of radio
   */
  @Dispatch()
  getPrograms(): ProgramsRequest {
    return new ProgramsRequest();
  }

  /**
   * Retrieve all playlists of radio
   */
  @Dispatch()
  getPlaylists(radioId: number): SchedulerAllPlaylistsRequest {
    return new SchedulerAllPlaylistsRequest(radioId);
  }

  /**
   * Retrieve all tracks of radio
   */
  @Dispatch()
  getTracks(radioId: number): SchedulerAllTracksRequest {
    return new SchedulerAllTracksRequest(radioId);
  }

  @Dispatch()
  getPlanning = (from: moment.Moment, to: moment.Moment) => {
    this.calendarApi.removeAllEvents();
    return new GetPlanningRequest({ from, to });
  };

  @Dispatch()
  dropSlot = (slot: Partial<Slot>) => new PlanningDropSlotRequest(slot);

  @Dispatch()
  setCurrentDay(day: moment.Moment) {
    return new SetCurrentDay(day);
  }

  @Dispatch()
  tooltipNoContent = () => new TooltipNoContent();
}
